import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonDictsBasics = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Key-Value Engine</div>
                <h1>Dictionaries: Power & Speed</h1>
                <p className="hero-lead">
                    In Python, everything is a dictionary. Modules, classes, namespacesit's all hash maps all the way down.
                    Understanding the Dictionary is understanding Python itself.
                </p>
            </div>

            <div className="content-section chapter-intro">
                <p>
                    A <code>dict</code> (Hash Map) is the most optimized data structure in Python.
                    It maps unique <strong>Keys</strong> to <strong>Values</strong>.
                </p>
                <p>
                    Historically, dictionaries were unordered and memory-hungry.
                    But in the Python 3.6+ era, a revolution called the "Compact Dict" changed everything.
                    They are now <strong>ordered by insertion</strong> and use ~30% less RAM.
                </p>

                <div className="learning-objectives">
                    <h3>What You'll Learn</h3>
                    <ul>
                        <li><strong>Modern Internals:</strong> How "Compact Dicts" work (Indices Array vs Entries Array).</li>
                        <li><strong>Safe Access:</strong> Why <code>.get()</code> is better than brackets.</li>
                        <li><strong>Merging:</strong> The modern <code>|</code> operator (Python 3.9+).</li>
                        <li><strong>Performance:</strong> O(1) average lookup speed.</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>The Revolution: Compact Dicts</h2>
                <p>
                    Before Python 3.6, a dictionary was a sparse table with lots of empty space.
                    Now, Python splits simple storage into two parts:
                </p>
                <ol>
                    <li><strong>Indices Array:</strong> A small table mapping hash-to-index.</li>
                    <li><strong>Entries Array:</strong> A dense list of <code>(hash, key, value)</code> tuples.</li>
                </ol>
                <p>
                    This is why <code>d = &#123;"a": 1, "b": 2&#125;</code> remembers that "a" came before "b". It's not just a side effect anymore; it's a language guarantee.
                </p>
            </div>

            <div className="content-section">
                <h2>Accessing Patterns: Safe vs Unsafe</h2>
                <p>
                    The most common error in beginner Python code is <code>KeyError</code>.
                    This happens when you assume data exists when it doesn't.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Avoiding KeyError Crashes</span>
                    </div>
                    <CodeBlock language="python" code={`user_data = {"id": 4209, "name": "Rohit"}

#  Unsafe: If 'email' is missing, the program CRASHES
# print(user_data["email"]) 
# Output: KeyError: 'email'

# Safe: Returns None if missing
email = user_data.get("email") 

# Safer: Returns a default value
role = user_data.get("role", "Guest")
print(f"Role: {role}")  # Output: Role: Guest`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Merging Dictionaries (The Modern Way)</h2>
                <p>
                    Merging configs or JSON responses is common. Python 3.9 gave us the beautiful Union Operator <code>|</code>.
                </p>
                <CodeBlock language="python" code={`default_config = {"theme": "dark", "notifications": True}
user_config = {"theme": "light"}

# The RIGHT side overwrites the LEFT side
final_config = default_config | user_config

print(final_config) 
# {'theme': 'light', 'notifications': True}`} />

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Legacy Code:</strong> In older codebases, you will see <code>&#123;**d1, **d2&#125;</code>. This is the "dictionary unpacking" hack. It works, but <code>|</code> is cleaner.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Valid Keys: Hashability Rule</h2>
                <p>
                    Just like Sets, Dictionary Keys must be <strong>immutable</strong> (hashable).
                    Values can be anything.
                </p>
                <CodeBlock language="python" code={`# Valid
d = {
    (1, 2): "Coordinate",  # Tuple is immutable
    "name": "String"       # String is immutable
}

#  Invalid
d = {
    [1, 2]: "List"         # List is mutable -&gt; CRASH
}`} />
            </div>

            <div className="content-section best-practices">
                <h2>Best Practices & Takeaways</h2>
                <div className="practice-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Use <code>.get()</code> for optional keys.</li>
                            <li>Use <code>.items()</code> to loop over key-value pairs at once.</li>
                            <li>Use <code>dict | dict</code> to merge.</li>
                        </ul>
                    </div>

                    <div className="practice-card">
                        <h3> Don't</h3>
                        <ul>
                            <li>Don't check <code>if key in d:</code> then access <code>d[key]</code>. Just use <code>d.get()</code>!</li>
                            <li>Don't modify a dict size while looping over it.</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default PythonDictsBasics;


